import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createPortal } from 'react-dom'
import { BrowserRouter } from 'react-router-dom'
import { motion } from 'framer-motion'
import App from './App.jsx'
import { useToaster, resolveValue } from 'react-hot-toast/headless'
import { useUIStore } from './hooks/useUIStore'
import './styles/globals.css'
import './i18n'

// Custom toast rendering (headless react-hot-toast + Framer Motion): each
// toast fades in in place — centered horizontally, just beneath the header —
// holds for its duration, then slides/fades out to the right.
const TOAST_TYPE_ACCENT = {
  success: '#22c55e',
  error: '#ef4444',
  loading: '#3b82f6',
  blank: '#6b7280',
  custom: '#6b7280',
}

// Resting spot: horizontally centered, just under the header. The header's
// rendered height varies (1-3 lines depending on breakpoint/content), so
// this reads the live measurement from useUIStore instead of a fixed guess.
const HEADER_MARGIN_PX = 16

function ToastItem({ t, index }) {
  const headerHeight = useUIStore((state) => state.headerHeight)
  // Some games publish their own second header row's bottom edge (e.g. Red
  // Sea's title/turn/moves bar) — when present, toasts clear that instead
  // of just the app's global header above it.
  const gameSubHeaderBottom = useUIStore((state) => state.gameSubHeaderBottom)
  const restBase = gameSubHeaderBottom ?? headerHeight
  const topOffset = restBase + HEADER_MARGIN_PX + index * 64 // stack additional simultaneous toasts below the first

  return (
    <motion.div
      role={t.ariaProps.role}
      aria-live={t.ariaProps['aria-live']}
      initial={{ opacity: 0, top: `${topOffset}px`, left: '50%', x: '-50%', scale: 1 }}
      animate={
        t.visible
          ? {
              opacity: 1,
              top: `${topOffset}px`,
              left: '50%',
              x: '-50%',
              scale: 1,
              transition: { duration: 0.4, ease: 'easeInOut' },
            }
          : {
              opacity: 0,
              top: `${topOffset}px`,
              left: '120%',
              x: '-50%',
              scale: 0.95,
              transition: { duration: 0.7, ease: 'easeInOut' },
            }
      }
      style={{
        position: 'fixed',
        zIndex: 9999,
        background: '#1a1a1a',
        color: '#ffffff',
        borderRadius: '8px',
        borderLeft: `4px solid ${TOAST_TYPE_ACCENT[t.type] || TOAST_TYPE_ACCENT.blank}`,
        padding: '12px 20px',
        maxWidth: 'calc(100vw - 32px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
        pointerEvents: 'none',
        ...t.style,
      }}
    >
      {resolveValue(t.message, t)}
    </motion.div>
  )
}

function ToasterPortal() {
  const { toasts } = useToaster({ duration: 5500 })

  return createPortal(
    <>
      {toasts.map((t, index) => (
        <ToastItem key={t.id} t={t} index={index} />
      ))}
    </>,
    document.body
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Suspense fallback={
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    }>
      <BrowserRouter>
        <App />
        <ToasterPortal />
      </BrowserRouter>
    </Suspense>
  </React.StrictMode>,
)
